import { useState, type ReactNode } from 'react';

import { InterruptChatDialog, useSocket, useTextChat } from '@mint-lib/ai-chat';

import './scrollbarStyle.css';

import { cn } from '@mint-lib/tw-utils';

import AgenticAiWelcomeMessage from '../../atoms/AgentAiWelcomeMessage/AgenticAiWelcomeMessage.jsx';
import AgenticAiAlert from '../../atoms/AgenticAiAlert/AgenticAiAlert.jsx';
import AgenticAiChatInput from '../../atoms/AgenticAiChatInput/AgenticAiChatInput.jsx';
import AgenticAiLink from '../../atoms/AgenticAiLink/AgenticAiLink.jsx';
import Messages from '../../atoms/AgenticAiMessages/Messages.jsx';
import AgenticAiSuggestedPrompts from '../../atoms/AgenticAiSuggestedPrompts/AgenticAiSuggestedPrompts.jsx';
import AgenticAiAgentsButton from '../AgenticAiAgents/AgenticAiAgentsButton.jsx';

type Props = {
  isFullScreen: boolean;
};
type ContentProps = {
  children: ReactNode;
  chatInput: ReactNode;
  link: ReactNode;
  prompts: ReactNode;
  welcomeMessage: ReactNode;
};

const AgenticAiChat = ({ isFullScreen }: Props) => {
  const { chatId } = useSocket();
  const { isChatHistoryLoading } = useTextChat();
  // The input hides together with the content that settles behind the
  // skeleton, so it can't pop in ahead of the messages.
  const [isChatSettling, setIsChatSettling] = useState(false);
  const ChatWrapper = isFullScreen ? ContentFullScreen : ContentCompact;

  return (
    <ChatWrapper
      welcomeMessage={<AgenticAiWelcomeMessage />}
      chatInput={
        !isChatHistoryLoading &&
        !isChatSettling && <AgenticAiChatInput isFullScreen={isFullScreen} />
      }
      link={<AgenticAiLink />}
      prompts={<AgenticAiSuggestedPrompts isFullScreen={isFullScreen} />}
    >
      <Messages key={chatId} onSettlingChange={setIsChatSettling} />
      <InterruptChatDialog chatId={chatId} />
    </ChatWrapper>
  );
};

const ContentCompact = ({
  children,
  chatInput,
  link,
  prompts,
  welcomeMessage,
}: ContentProps) => {
  const { chatId } = useSocket();

  if (chatId) {
    return (
      <div
        aria-label="agentic-content-compact"
        className="grid h-full min-w-0 flex-1 grid-rows-[minmax(0,1fr)_auto] overflow-hidden px-4"
      >
        <div className="chat-scrollbar mt-3 min-h-0 [scrollbar-width:none] [scrollbar-gutter:stable] overflow-x-auto">
          {children}
        </div>

        <div className="flex w-full flex-col items-center gap-2 pb-4">
          {chatInput}
          {link}
        </div>
      </div>
    );
  }

  return (
    <div
      aria-label="agentic-content-compact"
      className="flex h-full min-w-0 flex-1 flex-col items-center justify-between px-4"
    >
      {welcomeMessage}
      {chatInput}
      {prompts}
      {link}
    </div>
  );
};

const ContentFullScreen = ({
  children,
  chatInput,
  link,
  prompts,
  welcomeMessage,
}: ContentProps) => {
  const { chatId } = useSocket();

  return (
    <div
      className={cn(
        'flex h-full w-full max-w-full flex-row',
        !chatId && 'm-auto max-h-208',
      )}
    >
      <div
        aria-label="agentic-content-full-screen"
        className={cn(
          'flex h-full w-full flex-col items-center justify-start overflow-hidden',
          chatId
            ? 'chat-scrollbar rounded-lg border border-secondary-light'
            : 'px-30',
        )}
      >
        {chatId && (
          <div className="flex h-15 w-full max-w-[75vw] shrink-0 flex-col justify-start gap-4 pt-3 pr-[3%] pb-6 pl-[4%]">
            <AgenticAiAgentsButton />
          </div>
        )}
        <div
          className={cn(
            'flex h-full w-full flex-col pr-[3%] pl-[4%]',
            chatId
              ? 'max-w-[75vw] justify-between overflow-hidden'
              : 'max-w-201',
          )}
        >
          {chatId ? (
            <>
              <div className="min-h-0 overflow-y-auto">{children}</div>

              <div className="flex w-full flex-col items-center gap-4 pb-4">
                {chatInput}
                {link}
                <AgenticAiAlert />
              </div>
            </>
          ) : (
            <>
              {children}
              <div className="flex h-full w-full flex-col items-center justify-between gap-4 self-center">
                {welcomeMessage}
                {chatInput}
                {prompts}
                {link}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgenticAiChat;
